import type { ReactNode } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { useMocks } from '@config/env';
import { oidcConfig } from '@core/auth/oidcConfig';
import { queryClient } from '@shared/lib/queryClient';
import { ThemeProvider, useTheme } from '@shared/theme';

export function AppProviders({ children }: { children: ReactNode }) {
  const inner = (
    <QueryClientProvider client={queryClient}>
      {children}
      <ThemedToaster />
    </QueryClientProvider>
  );

  // In mock mode, skip the OIDC AuthProvider entirely so the SPA doesn't try to initialise
  // oidc-client-ts with placeholder Cognito metadata.
  const withAuth = useMocks ? inner : <AuthProvider {...oidcConfig}>{inner}</AuthProvider>;

  return <ThemeProvider>{withAuth}</ThemeProvider>;
}

/** Sonner's own theme defaults to OS preference; pin it to the app theme so toasts match the UI. */
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors closeButton theme={theme} />;
}
